import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, Search, X } from 'lucide-react';
import { Quote, QuoteItem, QuoteSection } from '../../types';
import { Button } from '../Button';
import { formatEur, formatQty } from '../../services/quoteOrderTranches';
import { quoteItemFromProjectService } from '../../services/quoteSupplementLogic';
import { loadSupplementContext, type SupplementContext } from '../../services/quoteSupplementService';
import { supplementProjectLabel, takenProjectServiceIds } from '../../services/supplementAcceptForm';
import { describeError } from '../../services/errorMessage';

/**
 * Nachtragsangebot: Positionen aus dem LV des Projekts übernehmen. Jede
 * gewählte Projektposition wird eine Angebotsposition mit Projektpreis,
 * Menge 1 und `targetServiceId` — bei der Annahme steigt dann deren
 * Vertragsmenge, statt eine neue Position anzuhängen (Vorschlag, im
 * Annahmedialog änderbar).
 *
 * Geschrieben wird hier nichts: onAdd übergibt die neuen Positionen dem
 * Editor, der sie wie eine Katalogposition einfügt.
 */
interface Props {
  projectId: string;
  quote: Pick<Quote, 'id' | 'details' | 'items'>;
  sections: QuoteSection[];
  /** Zielabschnitt beim Öffnen (Knopf im Abschnitt); fehlt = erster Abschnitt. */
  initialSectionId?: string;
  onCancel: () => void;
  onAdd: (items: QuoteItem[]) => void;
}

const inputClass =
  'w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1';

export const ProjectPositionPicker: React.FC<Props> = ({ projectId, quote, sections, initialSectionId, onCancel, onAdd }) => {
  const [ctx, setCtx] = useState<SupplementContext | null>(null);
  const [ctxError, setCtxError] = useState('');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set<string>());
  const [sectionId, setSectionId] = useState(() =>
    (initialSectionId && sections.some((s) => s.id === initialSectionId) ? initialSectionId : sections[0]?.id) || '');
  const [addError, setAddError] = useState('');

  useEffect(() => {
    let alive = true;
    loadSupplementContext(projectId, quote)
      .then((c) => { if (alive) setCtx(c); })
      .catch((e) => { if (alive) setCtxError(describeError(e)); });
    return () => { alive = false; };
    // Einmal je Projekt — die Liste filtert danach lokal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, quote.id]);

  // Schon übernommen (gleiche targetServiceId): markiert, aber wählbar.
  const taken = useMemo(() => takenProjectServiceIds(quote.items), [quote.items]);
  const services = useMemo(() => {
    const all = ctx?.services || [];
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((s) => `${s.lvPosition || ''} ${s.title || ''}`.toLowerCase().includes(q));
  }, [ctx, query]);

  const toggle = (id: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const handleAdd = () => {
    // Reihenfolge wie im Projekt-LV, nicht wie angeklickt.
    const chosen = (ctx?.services || []).filter((s) => selected.has(s.id));
    try {
      const items = chosen.map((s) => ({ ...quoteItemFromProjectService(s, crypto.randomUUID()), sectionId: sectionId || undefined }));
      onAdd(items);
    } catch (e) {
      setAddError(`Positionen konnten nicht übernommen werden: ${describeError(e)}`);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 animate-fade-in backdrop-blur-sm z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-slate-800">Projektposition übernehmen</h2>
            <p className="text-sm text-slate-500 truncate">
              {ctx ? `Projekt ${supplementProjectLabel(ctx)}` : 'Projekt wird geladen…'}
            </p>
          </div>
          <button onClick={onCancel} className="p-2 text-slate-400 hover:text-slate-600 rounded-xl" title="Abbrechen">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {!ctx && !ctxError && (
            <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Projekt wird geladen…</p>
          )}
          {ctxError && (
            <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{ctxError}</span>
            </div>
          )}
          {ctx?.blocker && (
            <div className="flex gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{ctx.blocker} Die Annahme als Nachtrag ist so gesperrt.</span>
            </div>
          )}

          {ctx && (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  autoFocus
                  className={`${inputClass} pl-9`}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="LV-Nr. oder Bezeichnung suchen…"
                />
              </div>
              <div className="border border-slate-200 rounded-xl overflow-hidden divide-y divide-slate-100">
                {services.length === 0 ? (
                  <p className="px-4 py-3 text-sm text-slate-500">
                    {(ctx.services || []).length === 0 ? 'Das Projekt hat keine Leistungspositionen.' : 'Keine Position gefunden.'}
                  </p>
                ) : (
                  services.map((s) => (
                    <label key={s.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-700 truncate">
                          <span className="font-mono text-xs text-slate-400 mr-2">{s.lvPosition}</span>
                          {s.title}
                          {s.is_optional && <span className="ml-2 text-[11px] font-bold text-amber-600">NEP</span>}
                          {taken.has(s.id) && <span className="ml-2 text-[11px] font-semibold text-teal-700">schon im Nachtrag</span>}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          Vertrag {formatQty(Number(s.quantityContracted) || 0)} · erbracht {formatQty(Number(s.quantityUsed) || 0)} {s.unit}
                        </div>
                      </div>
                      <span className="shrink-0 text-xs font-bold text-slate-600 tabular-nums">{formatEur(Number(s.unitPrice) || 0)}</span>
                    </label>
                  ))
                )}
              </div>
              <p className="text-xs text-slate-500 ml-1">
                Übernommen werden Bezeichnung, Einheit, LV-Nr. und Einzelpreis des Projekts, Menge 1. Bei der Annahme steigt die
                Vertragsmenge dieser Projektposition — ändert sich Einzelpreis oder Einheit, wird sie eine neue Position.
              </p>

              {sections.length > 0 && (
                <div>
                  <label className={labelClass}>In Abschnitt</label>
                  <select className={inputClass} value={sectionId} onChange={(e) => setSectionId(e.target.value)}>
                    <option value="">Ohne Zuordnung</option>
                    {sections.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
                  </select>
                </div>
              )}
            </>
          )}

          {addError && (
            <div className="flex gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{addError}</span>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-slate-200">
          <Button variant="secondary" onClick={onCancel}>Abbrechen</Button>
          <Button onClick={handleAdd} disabled={!ctx || selected.size === 0}>
            {selected.size > 1 ? `${selected.size} Positionen übernehmen` : 'Position übernehmen'}
          </Button>
        </div>
      </div>
    </div>
  );
};
